import React, { useState } from "react";
import { PieChartProps } from "../types/dashboard";
import { usePieChartData } from "../hooks/usePieChartData";
import NoDataMessage from "./parts/NoDataMessage";
import ChartContainer from "./parts/ChartContainer";
import DataSummary from "./parts/DataSummary";
import PieChartsHeader from "./parts/PieChartsHeader";
import { getReceivedLabels, getNoDataMessage } from "../utils/dashboardLabels";

const PieCharts: React.FC<PieChartProps> = ({ 
  Orders = 0, 
  amount = 0, 
  ReceivedAmount = 0, 
  OutstandingAmount = 0, 
  Discount = 0, 
  DeliveryFee = 0, 
  ParcelFee = 0, 
  tips = 0, 
  title = "Order Summary", 
  timeRange = "today", 
  businessType,
  todaysOrderReceived = 0, 
  outstandingLedgerReceived = 0,
  receivedLabels: propReceivedLabels
}) => {
  // Use centralized labels
  const defaultLabels = getReceivedLabels(timeRange as keyof typeof getReceivedLabels);
  const receivedLabels = propReceivedLabels || defaultLabels;
  const noDataMessage = getNoDataMessage(timeRange as keyof typeof getNoDataMessage);

  const { hasValidData, chartData } = usePieChartData({ 
    Orders, 
    amount, 
    ReceivedAmount, 
    OutstandingAmount, 
    Discount, 
    DeliveryFee, 
    ParcelFee, 
    tips, 
    timeRange, 
    businessType, 
    todaysOrderReceived, 
    outstandingLedgerReceived,
    receivedLabels
  });

  const [showDetails, setShowDetails] = useState(true);

  const handleToggleDetails = () => setShowDetails((prev) => !prev);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (!hasValidData) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] select-none"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;

    return (
      <div className="relative overflow-hidden rounded-xl bg-white/95 p-3 shadow-xl border border-slate-100 backdrop-blur-md min-w-[150px] transition-all">
        <div
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ backgroundColor: data.fill }}
        />
        <div className="pl-1.5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{data.name}</p>
          <p className="text-base font-bold text-slate-900 mt-0.5">
            {data.name === "Orders" ? `${data.value} orders` : `QAR ${data.value.toLocaleString()}`}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full h-full flex flex-col justify-between rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <PieChartsHeader
          title={title}
          onToggleDetails={handleToggleDetails}
          showDetails={showDetails}
        />

        <div className="min-h-[220px] h-[220px] relative w-full my-2 flex items-center justify-center">
          {!hasValidData && <NoDataMessage message={noDataMessage} />}
          <ChartContainer
            hasValidData={hasValidData}
            chartData={chartData}
            renderCustomizedLabel={renderCustomizedLabel}
            CustomTooltip={CustomTooltip}
          />
        </div>

        <div
          className={`grid transition-all duration-300 ease-in-out ${
            showDetails ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="pt-4 border-t border-slate-100">
              <DataSummary 
                chartData={chartData} 
                businessType={businessType}
                timeRange={timeRange}
                receivedLabels={receivedLabels}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 sm:px-6 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="font-medium text-slate-600">Live Updates</span>
        </div>
        <span className="font-medium text-slate-400 tracking-wide uppercase text-[10px]">
          Interactive
        </span>
      </div>
    </div>
  );
};

export default PieCharts;